import { useCallback, useEffect, useRef, useState } from 'react'
import type { InboundMessage, OutboundMessage, TicketData } from '../types'
import { setDeskOrg } from '../api/client'

export type BridgeStatus = 'waiting' | 'connected' | 'standalone' | 'url'

/**
 * Port of the original public/app.js bridge.
 *
 * This page is cross-origin to the widget that embeds it, so it cannot touch
 * the ZOHODESK SDK. Everything arrives over postMessage from the widget host.
 * Behaviour is deliberately identical to the version proven against a live
 * ticket, so the host half in extension.js needs no change to its message
 * contract.
 *
 * Opened directly rather than embedded, the ticket id is read from the query
 * string instead. That exists so a draft can be tested without Desk in the
 * loop — certificate prompts, developer mode and a real ticket open — and is
 * not a way for an agent to pick a ticket in normal use.
 *
 * It grants nothing: draftReply resolves the org server-side from the caller's
 * own Zoho token and scopes retrieval to it, so an id guessed from another org
 * still returns the existing wrong_org 404.
 */
export function useDeskBridge() {
  const [status, setStatus] = useState<BridgeStatus>('waiting')
  const [ticket, setTicket] = useState<TicketData | null>(null)
  const [log, setLog] = useState<string[]>([])

  // Ref so the message listener never needs re-binding on each log append.
  const appendLog = useRef((msg: string) => {
    const t = new Date().toISOString().slice(11, 19)
    setLog((prev) => [...prev, `${t}  ${msg}`])
  }).current

  const send = useCallback(
    (msg: OutboundMessage) => {
      // '*' is fine outbound — the payload carries nothing secret. Inbound is
      // filtered on e.source below.
      parent.postMessage(msg, '*')
      appendLog(`→ ${msg.type}`)
    },
    [appendLog],
  )

  const requestTicket = useCallback(() => {
    send({ type: 'REQUEST_TICKET_DATA' })
  }, [send])

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      // Only the frame that embedded us may drive this page.
      if (e.source !== parent) return
      const data = e.data as InboundMessage | undefined
      if (!data || !data.type) return

      appendLog(`← ${data.type}`)

      if (data.type === 'TICKET_DATA') {
        setStatus('connected')
        setTicket(data.ticket ?? null)
        // Which help desk this ticket lives in, for the calls that follow.
        if (data.ticket?.orgId) setDeskOrg(String(data.ticket.orgId))
      }
    }

    window.addEventListener('message', onMessage)

    if (parent === window) {
      const params = new URLSearchParams(window.location.search)
      const id = params.get('ticket')?.trim()

      if (id) {
        // subject is optional but worth passing: the backend joins it with the
        // first customer message to search the knowledge base, so without it a
        // test retrieves different chunks than the widget would.
        const subject = params.get('subject')?.trim() || undefined
        setTicket({ id, subject })
        setStatus('url')
        appendLog(`ticket ${id} from the URL, testing outside Desk`)
      } else {
        appendLog('not embedded; open this through the Desk widget')
        setStatus('standalone')
      }
    } else {
      parent.postMessage({ type: 'WIDGET_READY' } satisfies OutboundMessage, '*')
      appendLog('→ WIDGET_READY')
    }

    return () => window.removeEventListener('message', onMessage)
  }, [appendLog])

  return { status, ticket, log, requestTicket }
}
